import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  ApplicationRef,
  CUSTOM_ELEMENTS_SCHEMA,
  Injector,
  NgModule,
  isDevMode,
  provideZoneChangeDetection
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { APP_BASE_HREF, CommonModule } from "@angular/common";
import { PreloadAllModules, RouteReuseStrategy } from "@angular/router";

import {
  AuthInterceptor,
  EncryptorInterceptor,
  ErrorHandlerInterceptor,
  TenantInterceptor,
  TimeZoneInterceptor,
  TokenUrlInterceptor,
  UserInterceptor
} from "@overa/security";
import { PlatformInterceptor } from "./core/platform/platform.interceptor";
import { PlatformRouteReuseStrategy } from "./core/platform/platform-route-reuse.strategy";
import { PlatformTenantService } from "./core/platform/platform-tenant.service";
import {
  InitModule,
  OveraAppComponent,
  OveraAppRoutingModule,
} from "@overa/base";
import {
  AutoTracingModule,
  FILEMANAGER_SERVICE_TOKEN,
  FileManagerService,
  GeneralDataService as OveraGeneralDataService,
  GLOBALS_TOKEN,
  OveraDynamicTranslateService,
  OveraTranslateModule,
  SafePipeModule,
  STORAGE_SERVICE_TOKEN,
  StorageService,
  TENANT_SERVICE_TOKEN,
  UtcDateInterceptor,
  provideOveraErrorHandler,
  provideOveraViewPreviewGuard,
} from "@overa/shared";
import { OveraLeftSidebarModule } from "@overa/components/left-sidebar";
import { OveraTopbarModule } from "@overa/components/topbar";
import { OveraBreadcrumbModule } from "@overa/components/breadcrumb";
import { OveraResponsiveMenuModule } from "@overa/components/responsive-menu";
import { OveraMenubarModule } from "@overa/components/menubar";

import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { OAuthModule } from "angular-oauth2-oidc";
import { ToastrModule } from "ngx-toastr";
import { NgxSpinnerModule } from "ngx-spinner";

import { AppConfig } from "./app.config";
import { BackofficeGlobals } from "./utils/globals";
import { RoutesService, RoutesSettings } from "./routes-settings";
import { DynamicUICodeService } from "@overa/dynamic/common";
import { BackofficeDynamicUICodeService } from "./services/backoffice-dynamic-ui-code.service";
import { ThemeService } from "./services/theme.service";
import { LayoutComponent } from "./components/shared/layout/layout.component";
import {
  DYNAMIC_GUARD_TOKEN,
  DYNAMIC_MAINTENANCE_GUARD_TOKEN,
  OveraDynamicModule,
} from "@overa/dynamic/components";


import { OveraModule } from "./modules/overa.module";
import { PrimeNgModule } from "./modules/primeng.module";

import { OveraDataTableModule } from "@overa/components/datatable";
import { OveraUserAutoCompleteModule } from "@overa/components/user-autocomplete";
import { GoldenLayoutComponentService } from "@overa/components/golden-layout";
import { provideOveraNotifications } from "@overa/notification";
import { OveraAiChatPanelModule, provideOveraAiAssist } from "@overa/ai";


export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

const menuService = new RoutesService();
RoutesSettings.initialize(menuService);

@NgModule({
  declarations: [LayoutComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [OveraAppComponent],
  imports: [
    InitModule.setConfig(AppConfig),
    OveraTranslateModule.setConfigDynamic(
      localStorage.getItem("locale") || navigator.language || "es",
      true,
    ),
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,

    ReactiveFormsModule,
    NgxSpinnerModule,
    ToastrModule.forRoot({
      positionClass: "toast-bottom-right",
      timeOut: 5000,
      enableHtml: true,
    }),

    OveraDataTableModule,


    AutoTracingModule,
    OAuthModule.forRoot(),
    OveraDynamicModule.forRoot(),
    OveraModule,
    PrimeNgModule,
    OveraLeftSidebarModule,
    OveraTopbarModule,
    OveraBreadcrumbModule,
    // Asistente general de la consola (LayoutComponent, sección AiChat del appsettings de la UI).
    OveraAiChatPanelModule,
    OveraResponsiveMenuModule,
    OveraMenubarModule,
    OveraAppRoutingModule.forRoot(
      RoutesSettings.ROUTES,
      false,
      PreloadAllModules,
      "reload",
    ),
    SafePipeModule,
  ],
  providers: [
    // OJO: Angular 21 arranca zoneless por defecto — `PlatformRef.bootstrapModuleFactory`
    // inyecta `provideZonelessChangeDetectionInternal()` aunque la app no lo pida, y por
    // eso una escritura de estado de plantilla desde un `setTimeout`/`subscribe` que no
    // llame a `markForCheck()` deja de repintar hasta el siguiente evento.
    // Se revierte con este provider (los del NgModule ganan a los internos). En Activity está
    // activado y barrido: 13 rutas, 0 NG0100, sin saturación.
    // ⚠ AQUÍ NO SE HA VALIDADO EN EJECUCIÓN: el servicio de BackOffice no arranca en el entorno
    // de desarrollo (4300/5601 sin respuesta), así que solo hay verificación estática. Antes de
    // fiarse, recorrer las pantallas propias —sobre todo `dynamic-studio-shell`, que lleva Monaco—
    // vigilando NG0100 en consola y saturación de CPU. El primer intento en Activity destapó un
    // bucle latente en la UILib (getter que devolvía objeto nuevo → formio reconstruía → ~42 req/s).
    provideZoneChangeDetection({ eventCoalescing: true }),

    // Manejador global de errores. Recoge lo que Angular no unifica por su
    // cuenta: excepciones no controladas, promesas rechazadas sin `catch`
    // (también las que zone.js desvía fuera de la zona de Angular), errores
    // lanzados por librerías de terceros y recursos que no cargan. Importa
    // especialmente con Angular 21: una excepción dentro de la detección de
    // cambios congela la pantalla, y sin esto no queda traza con contexto.
    ...provideOveraErrorHandler(),

    { provide: APP_BASE_HREF, useFactory: getBaseUrlFactory },
    // Notificaciones en tiempo real y push que arranca el layout base (sin OveraNotificationModule no llegan solas).
    ...provideOveraNotifications(),
    // El asistente de IA de las pantallas de librerías (traducir menús de @overa/security): el módulo del chat solo se
    // importa en rutas diferidas, y la gestión de menús cuelga de otra.
    ...provideOveraAiAssist(),
    // Switching platform keeps the screen; without this the list stays with the previous platform's data.
    { provide: RouteReuseStrategy, useClass: PlatformRouteReuseStrategy },
    // EL PRIMERO: con la previsualización del editor de vistas abierta, bloquea las escrituras (423)
    // antes de que pasen por el resto (y sin el toast genérico de ErrorHandlerInterceptor).
    provideOveraViewPreviewGuard(),
    { provide: HTTP_INTERCEPTORS, useClass: TokenUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    // Junto al del inquilino porque hace lo mismo un nivel más arriba: añade el ámbito de la petición.
    { provide: HTTP_INTERCEPTORS, useClass: PlatformInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UserInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimeZoneInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UtcDateInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EncryptorInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    { provide: GLOBALS_TOKEN, useClass: BackofficeGlobals },
    // Every tenant selector lists the tenants of the platform being operated (see PlatformTenantService).
    { provide: TENANT_SERVICE_TOKEN, useClass: PlatformTenantService },
    { provide: STORAGE_SERVICE_TOKEN, useClass: StorageService },
    { provide: FILEMANAGER_SERVICE_TOKEN, useClass: FileManagerService },
    { provide: DynamicUICodeService, useClass: BackofficeDynamicUICodeService },

    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  static injector: Injector;

  constructor(
    private readonly translate: OveraDynamicTranslateService,
    private readonly injector: Injector,
    private readonly goldenLayoutComponentService: GoldenLayoutComponentService,
    private readonly applicationRef: ApplicationRef,
    // Se inyecta solo para instanciarlo: aplica la clase de tema al documento en su constructor. Sin
    // esto, `overa-shared.defaultTheme` no lo lee nadie y el preset `admin` queda inerte.
    private readonly themeService: ThemeService,
  ) {
    AppModule.injector = this.injector;
    this.translate.setTranslations();

  }
}

export function getBaseUrlFactory() {
  try {
    return document.getElementsByTagName("base")[0].href ?? "/";
  } catch {
    return "/";
  }
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}
